import { apiAgent } from "../../config/axiosConfig";
import { createAsyncThunk } from "@reduxjs/toolkit";

const getChat = createAsyncThunk(
  "getChat",
  async ({ aiChatId }, { rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/ai_chats/${aiChatId}`,
      });
      if (response.status === 200 && response?.data) {
        return response;
      } else {
        return rejectWithValue(response?.data);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const startChat = createAsyncThunk(
  "startChat",
  async ({ projectId, userMsg, aiModel }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/ai_chats",
        params: {
          project_id: projectId,
          user_message: userMsg,
          ai_model: aiModel,
        },
      });
      if (response.status === 200) {
        return response?.data;
      } else {
        return rejectWithValue(response?.data);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const postMsg = createAsyncThunk(
  "postMsg",
  async (
    { aiChatId, userMsg, saveInEnv, saveInMemory },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/ai_chat_messages",
        data: {
          ai_chat_id: aiChatId,
          user_message: userMsg,
          save_in_env: saveInEnv,
          save_in_memory: saveInMemory,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getMsg = createAsyncThunk(
  "getMsg",
  async ({ aiChatId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/ai_chat_messages",
        params: {
          ai_chat_id: aiChatId,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getOlderMsg = createAsyncThunk(
  "getOlderMsg",
  async ({ aiChatId, oldMsgId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/ai_chat_messages",
        params: {
          ai_chat_id: aiChatId,
          last_message_id: oldMsgId,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const addCollection = createAsyncThunk(
  "addCollection",
  async ({ aiChatId, collectionIds }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/ai_chats/select_collection`,
        data: {
          ai_chat_id: aiChatId,
          collection_ids: collectionIds,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const addEnvironment = createAsyncThunk(
  "addEnvironment",
  async ({ aiChatId, environmentId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/ai_chats/${aiChatId}`,
        data: {
          environment_set_id: environmentId,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const switchAIModel = createAsyncThunk(
  "switchAIModel",
  async ({ aiChatId, modelId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/ai_chats/${aiChatId}`,
        data: {
          ai_model: modelId,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const addEntitiesToChat = createAsyncThunk(
  "addEntitiesToChat",
  async ({ aiChatId, entityIds }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "PUT",
        url: `/api/v1/ai_chats/${aiChatId}`,
        data: {
          entity_ids: entityIds,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getAllSwagger = createAsyncThunk(
  "getAllSwagger",
  async ({ collectionId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/collections/${collectionId}/get_all_swagger`,
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const addTestScenarioRequest = createAsyncThunk(
  "addTestScenarioRequest",
  async ({ aiChatId, testScenarioId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/ai_chats/generate_request_data`,
        data: {
          test_scenario_id: testScenarioId,
          ai_chat_id: aiChatId,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const userFeedback = createAsyncThunk(
  "userFeedback",
  async (
    { aiChatId, aiChatMsgId, feedback, feedbackType },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: `/api/v1/ai_chat_message_reports`,
        data: {
          ai_chat_message_id: aiChatMsgId,
          ai_chat_id: aiChatId,
          feedback: feedback,
          feedback_type: feedbackType,
        },
      });
      if (response.status === 200) {
        return response;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getOldChatHistory = createAsyncThunk(
  "getOldChatHistory",
  async ({ projectId, page }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/ai_chats",
        params: {
          project_id: projectId,
          page: page,
        },
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const createTestScenariosGenerationForEntity = createAsyncThunk(
  "createTestScenariosGenerationForEntity",
  async ({ entityId, categories }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "POST",
        url: "/api/v1/test_scenario_generations",
        data: {
          entity_id: entityId,
          categories: categories,
        },
      });
      if (response.status === 201) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getTestScenariosGenerationDetail = createAsyncThunk(
  "getTestScenariosGenerationDetail",
  async ({ generationId }, { rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: `/api/v1/test_scenario_generations/${generationId}`,
      });
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const getAllTestScenariosForEntity = createAsyncThunk(
  "getAllTestScenariosForEntity",
  async ({ projectId, entityId, pageNumber }, { rejectWithValue }) => {
    try {
      const response = await apiAgent({
        method: "GET",
        url: "/api/v1/test_scenarios",
        params: {
          entity_id: entityId,
          project_id: projectId,
          page: pageNumber,
        },
      });
      if (response.status === 200) {
        return response?.data;
      } else {
        return rejectWithValue(response);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export {
  getChat,
  startChat,
  postMsg,
  getMsg,
  getOlderMsg,
  addCollection,
  addEnvironment,
  getAllSwagger,
  addTestScenarioRequest,
  userFeedback,
  getOldChatHistory,
  addEntitiesToChat,
  switchAIModel,
  createTestScenariosGenerationForEntity,
  getTestScenariosGenerationDetail,
  getAllTestScenariosForEntity,
};
